import { useRef, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import { getAllTransactions } from "services/transactionService";
import {
  loadReportTransactions,
  exportFinanceReport,
} from "./reportUtils";

export default function FinanceReport() {
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(1, "month").add(1, "day").startOf("day"));
  const [toDate, setToDate] = useState(() => dayjs().startOf("day"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const exporting = useRef(false);
  const validRange = Boolean(
    fromDate?.isValid() && toDate?.isValid() &&
    !fromDate.isAfter(toDate, "day") && !toDate.isAfter(dayjs(), "day")
  );

  const exportPdf = async () => {
    if (exporting.current) return;
    if (!validRange) {
      setError("Select valid From and To dates. From must be on or before To, and dates cannot be in the future.");
      return;
    }
    exporting.current = true;
    setBusy(true);
    setError("");
    try {
      const range = { fromdate: fromDate.format("YYYY-MM-DD"), todate: toDate.format("YYYY-MM-DD") };
      const rows = await loadReportTransactions(getAllTransactions, range);
      await exportFinanceReport({ rows, range });
    } catch (e) {
      setError(e?.message || "PDF could not be exported. Please try again.");
    } finally {
      exporting.current = false;
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <CustomDatePicker
          label="From"
          value={fromDate}
          onChange={(value) => { setFromDate(value); setError(""); }}
          width={160}
          maxDate={dayjs()}
          disabled={busy}
        />
        <CustomDatePicker
          label="To"
          value={toDate}
          onChange={(value) => { setToDate(value); setError(""); }}
          width={160}
          minDate={fromDate?.isValid() ? fromDate : undefined}
          maxDate={dayjs()}
          disabled={busy}
        />
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={busy || !validRange}
          onClick={exportPdf}
          sx={{
            backgroundColor: "#2D4F91",
            color: "#FFFFFF",
            fontWeight: 600,
            px: 3,
            height: 50,
            borderRadius: "10px",
            textTransform: "none",
            boxShadow: "0 4px 10px rgba(45, 79, 145, 0.25)",
            "&:hover": {
              backgroundColor: "#233F78",
              boxShadow: "0 6px 14px rgba(45, 79, 145, 0.35)",
            },
          }}
        >
          {busy ? "Exporting..." : "Export PDF"}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
